import {
  Package, Truck, BarChart3, Users, Warehouse, Globe, FileSpreadsheet,
  Smartphone, Shield, WifiOff, Calculator, MessageCircle, ChevronRight,
  ArrowRight, Pill, ClipboardList, CreditCard, TrendingUp, MapPin, Clock,
  X, Check, Store, Search, Heart, Activity
} from 'lucide-react';

export const translations = {
  en: {
    nav: { features: 'Features', solutions: 'Solutions', login: 'Login', getStarted: 'Get Started' },
    hero: {
      overline: 'PHARMACY MANAGEMENT PLATFORM',
      title: 'Connecting Pharmacies and Distributors,',
      titleAccent: 'Seamlessly.',
      subtitle: 'Manage products, deliveries, payments, and warehouse stock in one platform. Built for pharma companies and pharmacies in Central Asia.',
      cta: 'Start Free',
      ctaSecondary: 'See Features'
    },
    tabs: { distributors: 'For Distributors', pharmacies: 'For Pharmacies' },
    distFeatures: [
      { icon: 'Package', title: 'Product Catalog', desc: 'Manage products with regional pricing — set different prices by country, state, and district.', img: 'https://images.pexels.com/photos/6129585/pexels-photo-6129585.jpeg?auto=compress&cs=tinysrgb&w=1200' },
      { icon: 'Truck', title: 'Smart Deliveries', desc: 'Create deliveries with automatic warehouse stock deduction. Choose per delivery: warehouse or direct.', img: 'https://images.pexels.com/photos/6169132/pexels-photo-6169132.jpeg?auto=compress&cs=tinysrgb&w=1200' },
      { icon: 'Warehouse', title: 'Warehouse Management', desc: 'Real-time inventory tracking. Stock In/Out with full transaction history and audit trail.', img: 'https://images.unsplash.com/photo-1627915589334-14a3c3e3a741?w=1200&q=85' },
      { icon: 'BarChart3', title: 'Business Intelligence', desc: 'Inventory risk alerts, reliability scores, growth analytics, and district-level performance.', img: 'https://images.unsplash.com/photo-1647507490306-3411f230a750?w=1200&q=85' },
      { icon: 'Users', title: 'Medical Rep Tracking', desc: 'Assign MRs to territories. Mobile-optimized dashboard that works offline in remote areas.', img: 'https://images.unsplash.com/photo-1758599543139-a80cf330df35?w=1200&q=85' },
      { icon: 'FileSpreadsheet', title: 'Bulk Upload', desc: 'Import products, pharmacies, and warehouses from one Excel file. Drag-and-drop, instant update.', img: 'https://images.pexels.com/photos/8296982/pexels-photo-8296982.jpeg?auto=compress&cs=tinysrgb&w=1200' },
      { icon: 'Calculator', title: 'Account Management', desc: 'Running balances auto-calculated. Opening balance + deliveries - payments = current due.', img: 'https://images.pexels.com/photos/53621/calculator-calculation-insurance-finance-53621.jpeg?auto=compress&cs=tinysrgb&w=1200' },
      { icon: 'Clock', title: 'Month Control', desc: 'Open/close months, set targets, carry forward balances. Complete monthly reporting cycle.', img: 'https://images.pexels.com/photos/6170653/pexels-photo-6170653.jpeg?auto=compress&cs=tinysrgb&w=1200' },
    ],
    pharmFeatures: [
      { icon: 'Pill', title: 'View Deliveries', desc: 'See all deliveries received from connected distributors with full product details and amounts.', img: 'https://images.pexels.com/photos/3873146/pexels-photo-3873146.jpeg?auto=compress&cs=tinysrgb&w=1200' },
      { icon: 'CreditCard', title: 'Track Payments', desc: 'Monitor payments made and outstanding dues in real-time. Clear account history.', img: 'https://images.pexels.com/photos/164686/pexels-photo-164686.jpeg?auto=compress&cs=tinysrgb&w=1200' },
      { icon: 'ClipboardList', title: 'Company Catalog', desc: 'Browse connected company product catalogs with current pricing for your region.', img: 'https://images.unsplash.com/photo-1583944009439-57f0bb0168cf?w=1200&q=85' },
      { icon: 'TrendingUp', title: 'Account Overview', desc: 'Complete ledger view — every delivery, payment, and balance change at a glance.', img: 'https://images.unsplash.com/photo-1763038311036-6d18805537e5?w=1200&q=85' },
      { icon: 'MessageCircle', title: 'Direct Connection', desc: 'Stay connected with your distributor. Real-time updates on deliveries and account status.', img: 'https://images.pexels.com/photos/892757/pexels-photo-892757.jpeg?auto=compress&cs=tinysrgb&w=1200' },
      { icon: 'Shield', title: 'Secure Access', desc: 'Role-based access. Your data is private and only visible to you and your connected companies.', img: 'https://images.unsplash.com/photo-1724709162875-fe100dd0e04b?w=1200&q=85' },
    ],
    stats: [
      { value: '10x', label: 'Faster Operations' },
      { value: '0', label: 'Paper Needed' },
      { value: '24/7', label: 'Access Anywhere' },
      { value: '100%', label: 'Data Ownership' },
    ],
    offline: {
      sectionTitle: 'Works Offline. Syncs When Ready.',
      sectionSubtitle: 'Field reps stay productive in low-signal areas. Every delivery, payment, and visit is saved locally and synced automatically when back online.',
      online: 'Online',
      offlineLabel: 'Offline',
      noConnection: 'No connection? No problem.',
      noConnectionDesc: 'All actions are saved locally and queued for sync. MRs continue working without interruption — deliveries, payments, and visits are all recorded.',
      connected: 'Connected. All synced.',
      connectedDesc: 'All queued actions have been synced to the server. Real-time data is flowing — deliveries, payments, and visits are up to date.',
      audit: { title: 'Full audit trail', desc: 'Every action logged and traceable' },
      access: { title: 'Role-based access', desc: 'Granular permissions per user' },
      capability: { title: 'Offline-capable', desc: 'Works without internet connection' },
      mockup: {
        header: 'MR Mobile',
        offlineBadge: 'OFFLINE',
        onlineBadge: 'ONLINE',
        item1: 'Delivery to Apteka #14',
        item1time: '2m ago',
        item1status: 'QUEUED',
        item1synced: 'SYNCED',
        item2: 'Payment recorded $2,400',
        item2time: '5m ago',
        item2status: 'QUEUED',
        item2synced: 'SYNCED',
        item3: 'Visit log – Apteka #7',
        item3time: '12m ago',
        item3status: 'SYNCED',
        item4: 'New delivery created',
        item4time: 'now',
        item4status: 'QUEUED',
        item4synced: 'SYNCED',
        stillRunning: 'STILL RUNNING',
        stillRunningDesc: '4 actions saved locally – zero data loss',
        allSynced: 'ALL SYNCED',
        allSyncedDesc: '4 actions synced successfully – zero data loss',
        syncStatus: 'SYNC STATUS',
        syncPercent: '0%',
        syncComplete: '100%',
      }
    },
    cta: {
      title: 'Ready to modernize your pharmacy business?',
      subtitle: 'Join PharmX and leave spreadsheets behind.',
      button: 'Get Started Free'
    },
    footer: {
      copy: '2026 PharmX. All rights reserved.',
      tagline: 'Pharmacy Management Made Simple.'
    }
  },
  ru: {
    nav: { features: 'Возможности', solutions: 'Решения', login: 'Войти', getStarted: 'Начать' },
    hero: {
      overline: 'ПЛАТФОРМА УПРАВЛЕНИЯ АПТЕКАМИ',
      title: 'Связь аптек и дистрибьюторов,',
      titleAccent: 'Без сложностей.',
      subtitle: 'Управляйте товарами, доставками, оплатами и складскими запасами на одной платформе. Создано для фарм-компаний и аптек Центральной Азии.',
      cta: 'Начать бесплатно',
      ctaSecondary: 'Возможности'
    },
    tabs: { distributors: 'Для дистрибьюторов', pharmacies: 'Для аптек' },
    distFeatures: [
      { icon: 'Package', title: 'Каталог продуктов', desc: 'Управление товарами с региональными ценами — разные цены по странам, областям и районам.', img: 'https://images.pexels.com/photos/6129585/pexels-photo-6129585.jpeg?auto=compress&cs=tinysrgb&w=1200' },
      { icon: 'Truck', title: 'Умные доставки', desc: 'Создание доставок с автоматическим списанием склада. Выбор: склад или прямая доставка.', img: 'https://images.pexels.com/photos/6169132/pexels-photo-6169132.jpeg?auto=compress&cs=tinysrgb&w=1200' },
      { icon: 'Warehouse', title: 'Управление складом', desc: 'Отслеживание запасов в реальном времени. Приход/расход с историей операций.', img: 'https://images.unsplash.com/photo-1627915589334-14a3c3e3a741?w=1200&q=85' },
      { icon: 'BarChart3', title: 'Бизнес-аналитика', desc: 'Оповещения о рисках, оценки надежности, аналитика роста и региональные отчеты.', img: 'https://images.unsplash.com/photo-1647507490306-3411f230a750?w=1200&q=85' },
      { icon: 'Users', title: 'Контроль МП', desc: 'Назначение медпредставителей на территории. Мобильная панель, работающая офлайн.', img: 'https://images.unsplash.com/photo-1758599543139-a80cf330df35?w=1200&q=85' },
      { icon: 'FileSpreadsheet', title: 'Массовая загрузка', desc: 'Импорт товаров, аптек и складов из одного Excel-файла. Перетащите и обновите.', img: 'https://images.pexels.com/photos/8296982/pexels-photo-8296982.jpeg?auto=compress&cs=tinysrgb&w=1200' },
      { icon: 'Calculator', title: 'Управление счетами', desc: 'Автоматический расчет баланса. Начальный баланс + доставки - оплаты = текущий долг.', img: 'https://images.pexels.com/photos/53621/calculator-calculation-insurance-finance-53621.jpeg?auto=compress&cs=tinysrgb&w=1200' },
      { icon: 'Clock', title: 'Контроль месяца', desc: 'Открытие/закрытие месяцев, установка целей, перенос остатков.', img: 'https://images.pexels.com/photos/6170653/pexels-photo-6170653.jpeg?auto=compress&cs=tinysrgb&w=1200' },
    ],
    pharmFeatures: [
      { icon: 'Pill', title: 'Просмотр доставок', desc: 'Просматривайте все доставки от подключенных дистрибьюторов с полной информацией.', img: 'https://images.pexels.com/photos/3873146/pexels-photo-3873146.jpeg?auto=compress&cs=tinysrgb&w=1200' },
      { icon: 'CreditCard', title: 'Отслеживание оплат', desc: 'Контролируйте оплаты и задолженности в реальном времени. Полная история счета.', img: 'https://images.pexels.com/photos/164686/pexels-photo-164686.jpeg?auto=compress&cs=tinysrgb&w=1200' },
      { icon: 'ClipboardList', title: 'Каталог компании', desc: 'Просмотр каталога подключенной компании с актуальными ценами для вашего региона.', img: 'https://images.unsplash.com/photo-1583944009439-57f0bb0168cf?w=1200&q=85' },
      { icon: 'TrendingUp', title: 'Обзор аккаунта', desc: 'Полный просмотр книги учета — каждая доставка, оплата и изменение баланса.', img: 'https://images.unsplash.com/photo-1763038311036-6d18805537e5?w=1200&q=85' },
      { icon: 'MessageCircle', title: 'Прямая связь', desc: 'Оставайтесь на связи с дистрибьютором. Обновления в реальном времени.', img: 'https://images.pexels.com/photos/892757/pexels-photo-892757.jpeg?auto=compress&cs=tinysrgb&w=1200' },
      { icon: 'Shield', title: 'Безопасный доступ', desc: 'Ролевой доступ. Ваши данные приватны и видны только вам и подключенным компаниям.', img: 'https://images.unsplash.com/photo-1724709162875-fe100dd0e04b?w=1200&q=85' },
    ],
    stats: [
      { value: '10x', label: 'Быстрее операции' },
      { value: '0', label: 'Бумаги нужно' },
      { value: '24/7', label: 'Доступ отовсюду' },
      { value: '100%', label: 'Владение данными' },
    ],
    offline: {
      sectionTitle: 'Работает офлайн. Синхронизируется автоматически.',
      sectionSubtitle: 'Полевые представители продуктивны в зонах слабого сигнала. Каждая доставка, оплата и визит сохраняются локально и синхронизируются при появлении связи.',
      online: 'Онлайн',
      offlineLabel: 'Офлайн',
      noConnection: 'Нет связи? Не проблема.',
      noConnectionDesc: 'Все действия сохраняются локально и ставятся в очередь для синхронизации. МП продолжают работать без перерыва — доставки, оплаты и визиты записываются.',
      connected: 'Подключено. Всё синхронизировано.',
      connectedDesc: 'Все действия из очереди синхронизированы с сервером. Данные в реальном времени — доставки, оплаты и визиты обновлены.',
      audit: { title: 'Полный аудит', desc: 'Каждое действие записано и отслеживаемо' },
      access: { title: 'Ролевой доступ', desc: 'Гибкие права для каждого пользователя' },
      capability: { title: 'Офлайн режим', desc: 'Работает без интернет-соединения' },
      mockup: {
        header: 'MR Mobile',
        offlineBadge: 'ОФЛАЙН',
        onlineBadge: 'ОНЛАЙН',
        item1: 'Доставка в Аптеку #14',
        item1time: '2 мин назад',
        item1status: 'В ОЧЕРЕДИ',
        item1synced: 'СИНХР.',
        item2: 'Оплата записана $2,400',
        item2time: '5 мин назад',
        item2status: 'В ОЧЕРЕДИ',
        item2synced: 'СИНХР.',
        item3: 'Журнал визита – Аптека #7',
        item3time: '12 мин назад',
        item3status: 'СИНХР.',
        item4: 'Новая доставка создана',
        item4time: 'сейчас',
        item4status: 'В ОЧЕРЕДИ',
        item4synced: 'СИНХР.',
        stillRunning: 'РАБОТАЕТ',
        stillRunningDesc: '4 действия сохранены локально — потерь данных нет',
        allSynced: 'СИНХРОНИЗИРОВАНО',
        allSyncedDesc: '4 действия успешно синхронизированы — потерь данных нет',
        syncStatus: 'СТАТУС СИНХР.',
        syncPercent: '0%',
        syncComplete: '100%',
      }
    },
    cta: {
      title: 'Готовы модернизировать аптечный бизнес?',
      subtitle: 'Присоединяйтесь к PharmX и забудьте о таблицах.',
      button: 'Начать бесплатно'
    },
    footer: {
      copy: '2026 PharmX. Все права защищены.',
      tagline: 'Управление аптеками — просто.'
    }
  },
  tj: {
    nav: { features: 'Имконотхо', solutions: 'Хизматрасони', login: 'Даромадан', getStarted: 'Огоз кардан' },
    hero: {
      overline: 'ПЛАТФОРМАИ ИДОРАКУНИИ ДОРУХАНАХО',
      title: 'Пайвасти доруханахо ва дистрибюторхо,',
      titleAccent: 'Бе мушкили.',
      subtitle: 'Молхо, расонидан, пардохтхо ва анбори молро дар як платформа идора кунед. Барои ширкатхои дорусози ва доруханахои Осиёи Маркази.',
      cta: 'Ройгон огоз кунед',
      ctaSecondary: 'Имконотхо'
    },
    tabs: { distributors: 'Барои дистрибюторхо', pharmacies: 'Барои доруханахо' },
    distFeatures: [
      { icon: 'Package', title: 'Каталоги молхо', desc: 'Идоракунии молхо бо нархгузории минтакави — нархои гуногун аз руи кишвар, вилоят ва ноҳия.', img: 'https://images.pexels.com/photos/6129585/pexels-photo-6129585.jpeg?auto=compress&cs=tinysrgb&w=1200' },
      { icon: 'Truck', title: 'Расонидани зиракона', desc: 'Сохтани расонидан бо кам кардани худкори анбор. Интихоб: анбор ё мустаким.', img: 'https://images.pexels.com/photos/6169132/pexels-photo-6169132.jpeg?auto=compress&cs=tinysrgb&w=1200' },
      { icon: 'Warehouse', title: 'Идоракунии анбор', desc: 'Мониторинги захирахо дар вакти вокеи. Воридот/содирот бо таърихи муомилот.', img: 'https://images.unsplash.com/photo-1627915589334-14a3c3e3a741?w=1200&q=85' },
      { icon: 'BarChart3', title: 'Тахлили бизнес', desc: 'Огохихо дар бораи хатархо, рейтинги эътимод, тахлили рушд ва хисоботхои минтакави.', img: 'https://images.unsplash.com/photo-1647507490306-3411f230a750?w=1200&q=85' },
      { icon: 'Users', title: 'Назорати МП', desc: 'Таъйини намояндагони тибби ба минтакахо. Панели мобили, ки офлайн кор мекунад.', img: 'https://images.unsplash.com/photo-1758599543139-a80cf330df35?w=1200&q=85' },
      { icon: 'FileSpreadsheet', title: 'Боркунии оммави', desc: 'Воридоти молхо, доруханахо ва анборхо аз як файли Excel.', img: 'https://images.pexels.com/photos/8296982/pexels-photo-8296982.jpeg?auto=compress&cs=tinysrgb&w=1200' },
      { icon: 'Calculator', title: 'Идоракунии хисобхо', desc: 'Хисоби худкор. Бакияи аввала + расонидан - пардохт = карзи кунуни.', img: 'https://images.pexels.com/photos/53621/calculator-calculation-insurance-finance-53621.jpeg?auto=compress&cs=tinysrgb&w=1200' },
      { icon: 'Clock', title: 'Назорати мох', desc: 'Кушодан/пушидани мохо, гузоштани хадафхо, интиколи бакияхо.', img: 'https://images.pexels.com/photos/6170653/pexels-photo-6170653.jpeg?auto=compress&cs=tinysrgb&w=1200' },
    ],
    pharmFeatures: [
      { icon: 'Pill', title: 'Дидани расонидахо', desc: 'Тамоми расонидахоро аз дистрибюторхои пайвастшуда бо маълумоти пурра бинед.', img: 'https://images.pexels.com/photos/3873146/pexels-photo-3873146.jpeg?auto=compress&cs=tinysrgb&w=1200' },
      { icon: 'CreditCard', title: 'Пайгирии пардохтхо', desc: 'Пардохтхо ва карзхоро дар вакти вокеи назорат кунед.', img: 'https://images.pexels.com/photos/164686/pexels-photo-164686.jpeg?auto=compress&cs=tinysrgb&w=1200' },
      { icon: 'ClipboardList', title: 'Каталоги ширкат', desc: 'Каталоги ширкати пайвастшударо бо нархои кунуни барои минтакаи шумо бинед.', img: 'https://images.unsplash.com/photo-1583944009439-57f0bb0168cf?w=1200&q=85' },
      { icon: 'TrendingUp', title: 'Хулосаи хисоб', desc: 'Намуди пурраи дафтар — хар як расонидан, пардохт ва тагйироти бакия.', img: 'https://images.unsplash.com/photo-1763038311036-6d18805537e5?w=1200&q=85' },
      { icon: 'MessageCircle', title: 'Алокаи мустаким', desc: 'Бо дистрибютори худ дар алока бошед. Навсозихо дар вакти вокеи.', img: 'https://images.pexels.com/photos/892757/pexels-photo-892757.jpeg?auto=compress&cs=tinysrgb&w=1200' },
      { icon: 'Shield', title: 'Дастрасии бехатар', desc: 'Дастрасии мувофики наксх. Маълумоти шумо хусуси аст.', img: 'https://images.unsplash.com/photo-1724709162875-fe100dd0e04b?w=1200&q=85' },
    ],
    stats: [
      { value: '10x', label: 'Амалиёти тезтар' },
      { value: '0', label: 'Когаз лозим' },
      { value: '24/7', label: 'Дастраси аз хама чо' },
      { value: '100%', label: 'Соҳибии маълумот' },
    ],
    offline: {
      sectionTitle: 'Офлайн кор мекунад. Худ синхронизатсия мешавад.',
      sectionSubtitle: 'Намояндагони сахрои дар минтакахои сигнали суст хам самаранок кор мекунанд. Хар як расонидан, пардохт ва боздид захира шуда, хангоми пайвастшави синхронизатсия мешавад.',
      online: 'Онлайн',
      offlineLabel: 'Офлайн',
      noConnection: 'Пайвасти нест? Мушкиле нест.',
      noConnectionDesc: 'Хамаи амалхо дар чои худ захира шуда, барои синхронизатсия дар навбат мешаванд. МП бидуни монеа кор мекунанд — расонидан, пардохтхо ва боздидхо сабт мешаванд.',
      connected: 'Пайваст. Хама синхронизатсия шуд.',
      connectedDesc: 'Хамаи амалхои навбати бо сервер синхронизатсия шуданд. Маълумоти вокеи чори аст — расонидан, пардохтхо ва боздидхо навсозанд.',
      audit: { title: 'Аудити пурра', desc: 'Хар як амал сабт ва пайгиришаванда' },
      access: { title: 'Дастрасии мувофик', desc: 'Хукукхои дакик барои хар корбар' },
      capability: { title: 'Имконияти офлайн', desc: 'Бидуни пайвасти интернет кор мекунад' },
      mockup: {
        header: 'MR Mobile',
        offlineBadge: 'ОФЛАЙН',
        onlineBadge: 'ОНЛАЙН',
        item1: 'Расонидан ба Доруханаи #14',
        item1time: '2 дак пеш',
        item1status: 'ДАР НАВБАТ',
        item1synced: 'СИНХР.',
        item2: 'Пардохт сабт шуд $2,400',
        item2time: '5 дак пеш',
        item2status: 'ДАР НАВБАТ',
        item2synced: 'СИНХР.',
        item3: 'Журнали боздид – Доруханаи #7',
        item3time: '12 дак пеш',
        item3status: 'СИНХР.',
        item4: 'Расонидани нав сохта шуд',
        item4time: 'хозир',
        item4status: 'ДАР НАВБАТ',
        item4synced: 'СИНХР.',
        stillRunning: 'КОР МЕКУНАД',
        stillRunningDesc: '4 амал дар чои худ захира шуд — бидуни гум шудани маълумот',
        allSynced: 'СИНХРОНИЗАТСИЯ ШУД',
        allSyncedDesc: '4 амал бомуваффакият синхронизатсия шуд — бидуни гум шудани маълумот',
        syncStatus: 'ХОЛАТИ СИНХР.',
        syncPercent: '0%',
        syncComplete: '100%',
      }
    },
    cta: {
      title: 'Омодаед бизнеси доруханахоро муосир созед?',
      subtitle: 'Ба PharmX хамрох шавед ва аз чадвалхо даст кашед.',
      button: 'Ройгон огоз кунед'
    },
    footer: {
      copy: '2026 PharmX. Хамаи хукукхо махфузанд.',
      tagline: 'Идоракунии доруханахо — содда.'
    }
  }
};

export const iconMap = { Package, Truck, BarChart3, Users, Warehouse, Globe, FileSpreadsheet, Smartphone, Shield, WifiOff, Calculator, MessageCircle, Pill, ClipboardList, CreditCard, TrendingUp, MapPin, Clock };

export const COLORS = {
  bg: '#F4F9F6',
  surface: '#FFFFFF',
  primary: '#1a3a28',
  primaryHover: '#264a36',
  secondary: '#2E8B6E',
  secondaryHover: '#247a5e',
  text: '#1a3a28',
  textSecondary: '#5a6e64',
  textMuted: '#8a9e94',
  accent: '#3BA08A',
  sage: '#D4E8DE',
  green: '#2d6b48',
  border: 'rgba(26,58,40,0.1)',
};
